import React, { useEffect, useState } from "react";
import AppLayout from "../components/AppLayout";
import { api } from "../lib/api";
import { Plus, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["submitted", "in-talks", "confirmed", "posted", "declined"];

export default function Collabs() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ partner_name: "", handle: "", platform: "", status: "submitted", notes: "", contact: "", rate: "" });

  const load = async () => setItems((await api.get("/api/collabs")).data);
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ partner_name: "", handle: "", platform: "", status: "submitted", notes: "", contact: "", rate: "" }); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...it }); setOpen(true); };

  const save = async () => {
    if (!form.partner_name) return toast.error("Partner name required");
    if (editing) await api.put(`/api/collabs/${editing.id}`, form);
    else await api.post("/api/collabs", form);
    setOpen(false); load();
  };
  const remove = async (id) => { await api.delete(`/api/collabs/${id}`); load(); };
  const move = async (item, status) => { await api.put(`/api/collabs/${item.id}`, { status }); load(); };

  return (
    <AppLayout title="Collab pipeline" overline="080 · SUBMITTED → POSTED">
      <div className="flex justify-end mb-4">
        <button onClick={openNew} data-testid="collab-new-button" className="btn-primary inline-flex items-center gap-2">
          <Plus size={16} /> New collab
        </button>
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-5 gap-4" data-testid="collab-board">
        {STATUSES.map((s) => {
          const list = items.filter((i) => i.status === s);
          return (
            <div key={s} className="border border-zinc-900 bg-zinc-950 p-4 min-h-[260px]" data-testid={`collab-col-${s}`}>
              <div className="overline mb-4 text-[color:var(--gio-primary)]">{s} · {list.length}</div>
              <div className="space-y-3">
                {list.map((it) => (
                  <div key={it.id} className="group border border-zinc-800 p-3" data-testid={`collab-${it.id}`}>
                    <div className="flex justify-between gap-2">
                      <div className="text-white text-sm font-medium">{it.partner_name}</div>
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition">
                        <button onClick={() => openEdit(it)} className="text-zinc-500 hover:text-[color:var(--gio-primary)]"><Pencil size={14} /></button>
                        <button onClick={() => remove(it.id)} className="text-zinc-500 hover:text-[color:var(--gio-primary)]"><Trash2 size={14} /></button>
                      </div>
                    </div>
                    {it.handle && <div className="overline mt-1">@{it.handle} · {it.platform}</div>}
                    {it.rate && <div className="text-[color:var(--gio-secondary)] text-xs mt-1 font-mono-ui">{it.rate}</div>}
                    {it.notes && <div className="text-zinc-500 text-xs mt-2 line-clamp-3">{it.notes}</div>}
                    <div className="flex gap-1 mt-3 flex-wrap">
                      {STATUSES.filter(x => x !== s).map((x) => (
                        <button key={x} onClick={() => move(it, x)} data-testid={`collab-move-${it.id}-${x}`}
                          className="text-[10px] font-mono-ui text-zinc-600 hover:text-[color:var(--gio-primary)] border border-zinc-800 px-1.5 py-0.5 uppercase tracking-wider">
                          → {x}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
                {list.length === 0 && <div className="text-zinc-700 text-xs">—</div>}
              </div>
            </div>
          );
        })}
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" data-testid="collab-modal">
          <div className="bg-zinc-950 border border-zinc-800 max-w-lg w-full p-6">
            <div className="overline mb-4 text-[color:var(--gio-primary)]">{editing ? "EDIT" : "NEW"} · COLLAB</div>
            <F l="Partner name"><input data-testid="col-name" value={form.partner_name} onChange={(e) => setForm({ ...form, partner_name: e.target.value })} className="gio-input" /></F>
            <F l="Handle"><input data-testid="col-handle" value={form.handle} onChange={(e) => setForm({ ...form, handle: e.target.value })} className="gio-input" /></F>
            <F l="Platform"><input data-testid="col-platform" value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })} className="gio-input" /></F>
            <F l="Status">
              <select data-testid="col-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="gio-input">
                {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </F>
            <F l="Contact"><input data-testid="col-contact" value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} placeholder="email / dm..." className="gio-input" /></F>
            <F l="Rate / terms"><input data-testid="col-rate" value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} placeholder="$500 + revshare..." className="gio-input" /></F>
            <F l="Notes"><textarea data-testid="col-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className="gio-input" /></F>
            <div className="flex gap-3 mt-4">
              <button onClick={save} className="btn-primary flex-1" data-testid="col-save-button">Save</button>
              <button onClick={() => setOpen(false)} className="btn-ghost" data-testid="col-cancel-button">Cancel</button>
            </div>
          </div>
        </div>
      )}
      <style>{`.gio-input { width: 100%; background: #000; border: 1px solid #27272a; color: white; padding: 10px; font-size: 13px; outline: none; } .gio-input:focus { border-color: var(--gio-primary); }`}</style>
    </AppLayout>
  );
}

function F({ l, children }) { return <div className="mb-3"><div className="overline mb-2">{l}</div>{children}</div>; }
